import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import * as XLSX from 'xlsx';
import axiosInstance from '../../utils/axiosInstance';

// ── Constants ─────────────────────────────────────────────────────────────────
const REPEAT_COLOR  = '#ef4444';
const NOSUB_COLOR   = '#f59e0b';
const TABS = [
  { key: 'lecturer', label: 'Lecturer-wise' },
  { key: 'subject',  label: 'Subject-wise' },
  { key: 'batch',    label: 'Batch-wise' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(s));
}

function buildAggregations(courses) {
  const lecturerMap = new Map();
  const subjectMap  = new Map();
  const batchMap    = new Map();

  courses.forEach((course) => {
    course.batches.forEach((batch) => {
      const batchKey = `${batch.batchInternalName || batch.batchName}`;
      if (!batchMap.has(batchKey)) {
        batchMap.set(batchKey, {
          label: batchKey,
          courseName: course.courseShortName || course.courseName,
          batchId: batch.batchId,
          repeat: [], notSubmitted: [],
        });
      }
      const batchEntry = batchMap.get(batchKey);

      batch.subjects.forEach((subj) => {
        const lecturer = subj.lecturerName || '(No Lecturer)';

        // — Lecturer map —
        if (!lecturerMap.has(lecturer)) {
          lecturerMap.set(lecturer, { label: lecturer, repeat: [], notSubmitted: [], subjects: new Map() });
        }
        const lec = lecturerMap.get(lecturer);
        if (!lec.subjects.has(subj.batchSubjectId)) {
          lec.subjects.set(subj.batchSubjectId, {
            subjectName: subj.subjectName,
            subjectShortName: subj.subjectShortName,
            batchName: batchKey,
            courseName: course.courseShortName || course.courseName,
            actualSubmissionDate: subj.actualSubmissionDate,
            plannedSubmissionDate: subj.plannedSubmissionDate,
            repeat: [], notSubmitted: [],
          });
        }
        const lecSubj = lec.subjects.get(subj.batchSubjectId);
        subj.repeat.forEach((s) => { lec.repeat.push({ ...s, subjectName: subj.subjectName, batchName: batchKey, courseName: course.courseShortName || course.courseName }); lecSubj.repeat.push(s); });
        subj.notSubmitted.forEach((s) => { lec.notSubmitted.push({ ...s, subjectName: subj.subjectName, batchName: batchKey, courseName: course.courseShortName || course.courseName }); lecSubj.notSubmitted.push(s); });

        // — Subject map —
        const subjKey = `${subj.batchSubjectId}`;
        if (!subjectMap.has(subjKey)) {
          subjectMap.set(subjKey, {
            label: subj.subjectName,
            shortName: subj.subjectShortName,
            lecturerName: subj.lecturerName,
            batchSubjectId: subj.batchSubjectId,
            actualSubmissionDate: subj.actualSubmissionDate,
            plannedSubmissionDate: subj.plannedSubmissionDate,
            repeat: [], notSubmitted: [],
          });
        }
        const subjEntry = subjectMap.get(subjKey);
        subj.repeat.forEach((s) => subjEntry.repeat.push({ ...s, batchName: batchKey, courseName: course.courseShortName || course.courseName }));
        subj.notSubmitted.forEach((s) => subjEntry.notSubmitted.push({ ...s, batchName: batchKey, courseName: course.courseShortName || course.courseName }));

        // — Batch map —
        subj.repeat.forEach((s) => batchEntry.repeat.push({ ...s, subjectName: subj.subjectName }));
        subj.notSubmitted.forEach((s) => batchEntry.notSubmitted.push({ ...s, subjectName: subj.subjectName }));
      });
    });
  });

  const toChartRows = (map) =>
    Array.from(map.values())
      .map((e) => ({ ...e, repeatCount: e.repeat.length, notSubmittedCount: e.notSubmitted.length, total: e.repeat.length + e.notSubmitted.length }))
      .sort((a, b) => b.total - a.total);

  return {
    byLecturer: toChartRows(lecturerMap),
    bySubject:  toChartRows(subjectMap),
    byBatch:    toChartRows(batchMap),
  };
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const repeat  = payload.find((p) => p.dataKey === 'repeatCount');
  const notSub  = payload.find((p) => p.dataKey === 'notSubmittedCount');
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-lg text-sm">
      <p className="mb-1 font-semibold text-slate-800 max-w-[220px] truncate">{label}</p>
      {repeat  && <p className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" /><span className="text-slate-600">Repeat:</span> <span className="font-bold text-red-600">{repeat.value}</span></p>}
      {notSub  && <p className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" /><span className="text-slate-600">Not Submitted:</span> <span className="font-bold text-amber-600">{notSub.value}</span></p>}
    </div>
  );
}

// ── Detail content (shared by chart expand + modal) ──────────────────────────
function DetailContent({ entry, groupKey, subTab, setSubTab }) {
  const allStudents = useMemo(() => [
    ...entry.repeat.map((s) => ({ ...s, _type: 'Repeat' })),
    ...entry.notSubmitted.map((s) => ({ ...s, _type: 'Not Submitted' })),
  ], [entry]);

  const students = subTab === 'repeat' ? entry.repeat
    : subTab === 'notSubmitted' ? entry.notSubmitted
    : allStudents;

  const isLecturer = groupKey === 'lecturer';
  const subjectsArr = isLecturer && entry.subjects ? Array.from(entry.subjects.values()) : null;
  const total = entry.repeat.length + entry.notSubmitted.length;

  return (
    <>
      {/* Sub-tabs */}
      <div className="mb-3 flex gap-1.5 flex-wrap">
        <button
          onClick={() => setSubTab('all')}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${subTab === 'all' ? 'bg-slate-700 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
        >
          All ({total})
        </button>
        <button
          onClick={() => setSubTab('repeat')}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${subTab === 'repeat' ? 'bg-red-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
        >
          Repeat ({entry.repeat.length})
        </button>
        <button
          onClick={() => setSubTab('notSubmitted')}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${subTab === 'notSubmitted' ? 'bg-amber-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
        >
          Not Submitted ({entry.notSubmitted.length})
        </button>
      </div>

      {/* Lecturer: subject pills */}
      {isLecturer && subjectsArr?.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {subjectsArr.map((s, i) => (
            <span key={i} className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-600">
              {s.subjectName}
              <span className="ml-1 font-semibold text-red-500">{s.repeat.length}R</span>
              {' '}<span className="font-semibold text-amber-500">{s.notSubmitted.length}N</span>
            </span>
          ))}
        </div>
      )}

      {students.length === 0 ? (
        <p className="py-2 text-xs text-slate-400">No students in this category.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">#</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Reg No.</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Name</th>
                {subTab === 'all' && <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Status</th>}
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Subject</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Batch</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-400">Course</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{s.registrationNumber || '—'}</td>
                  <td className="px-3 py-2 text-xs font-medium text-slate-800">{s.studentName || '—'}</td>
                  {subTab === 'all' && (
                    <td className="px-3 py-2 text-xs">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${s._type === 'Repeat' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {s._type}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs text-slate-600">{s.subjectName || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{s.batchName || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{s.courseName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Detail table (chart expand) ───────────────────────────────────────────────
function DetailTable({ entry, groupKey }) {
  const [subTab, setSubTab] = useState('repeat');
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <h3 className="mb-3 font-semibold text-slate-800">{entry.label}</h3>
      <DetailContent entry={entry} groupKey={groupKey} subTab={subTab} setSubTab={setSubTab} />
    </div>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────────
function DetailModal({ entry, groupKey, onClose }) {
  const [subTab, setSubTab]               = useState('all');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState('');

  // All unique subjects across repeat + notSubmitted
  const allSubjects = useMemo(() => {
    const seen = new Set();
    const list = [];
    [...entry.repeat, ...entry.notSubmitted].forEach((s) => {
      if (s.subjectName && !seen.has(s.subjectName)) {
        seen.add(s.subjectName);
        list.push(s.subjectName);
      }
    });
    return list.sort();
  }, [entry]);

  const hasSubjectFilter = allSubjects.length > 1;
  const activeSubjects   = selectedSubjects.length ? selectedSubjects : allSubjects;

  const filterBySubject = useCallback((list) =>
    selectedSubjects.length ? list.filter((s) => selectedSubjects.includes(s.subjectName)) : list,
    [selectedSubjects]
  );

  // Filtered entry passed to DetailContent
  const filteredEntry = useMemo(() => ({
    ...entry,
    repeat:       filterBySubject(entry.repeat),
    notSubmitted: filterBySubject(entry.notSubmitted),
  }), [entry, filterBySubject]);

  const allStudentsFiltered = useMemo(() => [
    ...filteredEntry.repeat.map((s) => ({ ...s, _type: 'Repeat' })),
    ...filteredEntry.notSubmitted.map((s) => ({ ...s, _type: 'Not Submitted' })),
  ], [filteredEntry]);

  const toggleSubject = (name) =>
    setSelectedSubjects((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );

  const visibleSubjectOptions = subjectSearch.trim()
    ? allSubjects.filter((s) => s.toLowerCase().includes(subjectSearch.toLowerCase()))
    : allSubjects;

  const handleDownload = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const colsAll  = ['#', 'Reg No.', 'Name', 'Status', 'Subject', 'Batch', 'Course'];
    const colsBase = ['#', 'Reg No.', 'Name', 'Subject', 'Batch', 'Course'];

    const toRowsAll  = (list) => [colsAll,  ...list.map((s, i) => [i + 1, s.registrationNumber || '', s.studentName || '', s._type || '', s.subjectName || '', s.batchName || '', s.courseName || ''])];
    const toRowsBase = (list) => [colsBase, ...list.map((s, i) => [i + 1, s.registrationNumber || '', s.studentName || '', s.subjectName || '', s.batchName || '', s.courseName || ''])];

    if (subTab === 'all') {
      if (allStudentsFiltered.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toRowsAll(allStudentsFiltered)), 'All Students');
    } else if (subTab === 'repeat') {
      if (filteredEntry.repeat.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toRowsBase(filteredEntry.repeat)), 'Repeat');
    } else {
      if (filteredEntry.notSubmitted.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toRowsBase(filteredEntry.notSubmitted)), 'Not Submitted');
    }

    const safe     = entry.label.replace(/[\\/:*?"<>|]/g, '_').slice(0, 30);
    const tabLabel = subTab === 'all' ? 'All' : subTab === 'repeat' ? 'Repeat' : 'NotSubmitted';
    XLSX.writeFile(wb, `HND_${tabLabel}_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [entry, subTab, filteredEntry, allStudentsFiltered]);

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={handleBackdrop}>
      <div className="flex w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl" style={{ maxHeight: '90vh' }}>

        {/* Modal header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {groupKey === 'lecturer' ? 'Lecturer' : groupKey === 'subject' ? 'Subject' : 'Batch'} Detail
            </p>
            <h2 className="font-bold text-slate-800">{entry.label}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 rounded-lg border border-green-300 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 transition hover:bg-green-100"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1m-4-4-4 4m0 0-4-4m4 4V4" />
              </svg>
              Download Excel
            </button>
            <button onClick={onClose} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-100">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Subject filter bar — only when multiple subjects */}
        {hasSubjectFilter && (
          <div className="shrink-0 border-b border-slate-100 bg-slate-50 px-5 py-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Filter by Subject</p>
              {selectedSubjects.length > 0 && (
                <button
                  onClick={() => setSelectedSubjects([])}
                  className="text-[11px] font-medium text-violet-600 hover:underline"
                >
                  Clear ({selectedSubjects.length} selected)
                </button>
              )}
            </div>
            {/* Search */}
            <div className="mb-2 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1">
              <svg className="h-3.5 w-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                value={subjectSearch}
                onChange={(e) => setSubjectSearch(e.target.value)}
                placeholder="Search subjects…"
                className="w-full bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none"
              />
              {subjectSearch && (
                <button onClick={() => setSubjectSearch('')} className="text-slate-400 hover:text-slate-600">
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            {/* Subject pills */}
            <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
              {visibleSubjectOptions.map((subj) => {
                const active = selectedSubjects.includes(subj);
                return (
                  <button
                    key={subj}
                    onClick={() => toggleSubject(subj)}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition
                      ${active
                        ? 'border-violet-400 bg-violet-100 text-violet-800'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50'}`}
                  >
                    {subj}
                  </button>
                );
              })}
              {visibleSubjectOptions.length === 0 && (
                <p className="text-[11px] text-slate-400">No matches</p>
              )}
            </div>
          </div>
        )}

        {/* Modal body */}
        <div className="overflow-y-auto px-5 py-4">
          {selectedSubjects.length > 0 && (
            <p className="mb-2 text-[11px] text-slate-400">
              Showing {activeSubjects.length} of {allSubjects.length} subject{allSubjects.length !== 1 ? 's' : ''}
              {' · '}
              <span className="font-semibold text-slate-600">
                {filteredEntry.repeat.length + filteredEntry.notSubmitted.length} student{filteredEntry.repeat.length + filteredEntry.notSubmitted.length !== 1 ? 's' : ''}
              </span>
            </p>
          )}
          <DetailContent entry={filteredEntry} groupKey={groupKey} subTab={subTab} setSubTab={setSubTab} />
        </div>
      </div>
    </div>
  );
}

// ── Chart panel ───────────────────────────────────────────────────────────────
function ChartPanel({ rows, groupKey }) {
  const [selected, setSelected] = useState(null);

  const chartData = useMemo(() =>
    rows.map((r) => ({ name: r.label, repeatCount: r.repeatCount, notSubmittedCount: r.notSubmittedCount })),
    [rows]
  );

  const handleBarClick = useCallback((data) => {
    const entry = rows.find((r) => r.label === data.name);
    setSelected((prev) => prev?.label === entry?.label ? null : entry);
  }, [rows]);

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <svg className="mb-2 h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
        </svg>
        <p className="text-sm">No data to display.</p>
      </div>
    );
  }

  const barWidth = Math.max(28, Math.min(60, Math.floor(600 / rows.length)));

  return (
    <div>
      <p className="mb-3 text-xs text-slate-400">Click a bar to see student details.</p>
      <div className="overflow-x-auto">
        <div style={{ minWidth: rows.length * (barWidth + 32) + 80 }}>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ top: 16, right: 20, left: 0, bottom: rows.length > 6 ? 100 : 60 }}
              onClick={(e) => e?.activePayload?.[0] && handleBarClick({ name: e.activeLabel })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#64748b' }}
                interval={0}
                angle={rows.length > 5 ? -38 : 0}
                textAnchor={rows.length > 5 ? 'end' : 'middle'}
                height={rows.length > 5 ? 90 : 50}
              />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="repeatCount" name="Repeat" fill={REPEAT_COLOR} radius={[4, 4, 0, 0]} maxBarSize={barWidth} style={{ cursor: 'pointer' }}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={selected?.label === entry.name ? '#b91c1c' : REPEAT_COLOR} />
                ))}
              </Bar>
              <Bar dataKey="notSubmittedCount" name="Not Submitted" fill={NOSUB_COLOR} radius={[4, 4, 0, 0]} maxBarSize={barWidth} style={{ cursor: 'pointer' }}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={selected?.label === entry.name ? '#b45309' : NOSUB_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {selected && <DetailTable entry={selected} groupKey={groupKey} />}
    </div>
  );
}

// ── Excel export ──────────────────────────────────────────────────────────────
function buildExcel(agg) {
  const wb = XLSX.utils.book_new();

  const summaryRows = [['Dimension', 'Name', 'Repeat', 'Not Submitted', 'Total']];
  agg.byLecturer.forEach((r) => summaryRows.push(['Lecturer', r.label, r.repeatCount, r.notSubmittedCount, r.total]));
  agg.bySubject.forEach((r)  => summaryRows.push(['Subject',  r.label, r.repeatCount, r.notSubmittedCount, r.total]));
  agg.byBatch.forEach((r)    => summaryRows.push(['Batch',    r.label, r.repeatCount, r.notSubmittedCount, r.total]));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

  const repeatRows = [['Lecturer', 'Course', 'Batch', 'Subject', 'Reg No.', 'Student Name']];
  agg.byLecturer.forEach((lec) =>
    lec.repeat.forEach((s) =>
      repeatRows.push([lec.label, s.courseName, s.batchName, s.subjectName, s.registrationNumber, s.studentName])
    )
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(repeatRows), 'Repeat Students');

  const notSubRows = [['Lecturer', 'Course', 'Batch', 'Subject', 'Reg No.', 'Student Name']];
  agg.byLecturer.forEach((lec) =>
    lec.notSubmitted.forEach((s) =>
      notSubRows.push([lec.label, s.courseName, s.batchName, s.subjectName, s.registrationNumber, s.studentName])
    )
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(notSubRows), 'Not Submitted Students');

  const subjRows = [['Subject', 'Short Name', 'Lecturer', 'Planned Deadline', 'Actual Deadline', 'Repeat', 'Not Submitted']];
  agg.bySubject.forEach((s) =>
    subjRows.push([s.label, s.shortName || '', s.lecturerName || '', fmtDate(s.plannedSubmissionDate), fmtDate(s.actualSubmissionDate), s.repeatCount, s.notSubmittedCount])
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(subjRows), 'Subject Summary');

  return wb;
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HndSubmissionAnalyseView() {
  const [activeTab, setActiveTab] = useState('lecturer');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [modalEntry, setModalEntry] = useState(null);

  const params = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const p = {};
    if (sp.get('courseIds'))     p.courseIds     = sp.get('courseIds');
    if (sp.get('batchIds'))      p.batchIds      = sp.get('batchIds');
    if (sp.get('batchStatuses')) p.batchStatuses = sp.get('batchStatuses');
    return p;
  }, []);

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/hnd-submission-analysis', { params })
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load data.'))
      .finally(() => setLoading(false));
  }, [params]);

  const agg = useMemo(() => {
    if (!data?.courses?.length) return null;
    return buildAggregations(data.courses);
  }, [data]);

  const handleDownload = useCallback(() => {
    if (!agg) return;
    const wb = buildExcel(agg);
    XLSX.writeFile(wb, `HND_Submission_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [agg]);

  const totalRepeat       = agg?.byLecturer.reduce((n, r) => n + r.repeatCount, 0) ?? 0;
  const totalNotSubmitted = agg?.byLecturer.reduce((n, r) => n + r.notSubmittedCount, 0) ?? 0;

  const activeRows = agg
    ? activeTab === 'lecturer' ? agg.byLecturer
    : activeTab === 'subject'  ? agg.bySubject
    : agg.byBatch
    : [];

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-6 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">HND Submission Analysis</h1>
              {agg && (
                <p className="text-[11px] text-slate-500">
                  <span className="font-semibold text-red-600">{totalRepeat} Repeat</span>
                  {' · '}
                  <span className="font-semibold text-amber-600">{totalNotSubmitted} Not Submitted</span>
                  {' across '}
                  {agg.byLecturer.length} lecturer{agg.byLecturer.length !== 1 ? 's' : ''},&nbsp;
                  {agg.bySubject.length} subject{agg.bySubject.length !== 1 ? 's' : ''},&nbsp;
                  {agg.byBatch.length} batch{agg.byBatch.length !== 1 ? 'es' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {agg && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 rounded-lg border border-green-300 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 transition hover:bg-green-100"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1m-4-4-4 4m0 0-4-4m4 4V4" />
                </svg>
                Download Excel
              </button>
            )}
            <button
              onClick={() => window.close()}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="mx-auto max-w-[1400px] px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <svg className="mr-2 h-5 w-5 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
            </svg>
            <span className="text-sm">Loading analysis…</span>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && agg && (
          <>
            {/* Summary cards */}
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-red-400">Total Repeat</p>
                <p className="mt-0.5 text-3xl font-bold text-red-700">{totalRepeat}</p>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-500">Not Submitted</p>
                <p className="mt-0.5 text-3xl font-bold text-amber-700">{totalNotSubmitted}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Lecturers</p>
                <p className="mt-0.5 text-3xl font-bold text-slate-800">{agg.byLecturer.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Subjects</p>
                <p className="mt-0.5 text-3xl font-bold text-slate-800">{agg.bySubject.length}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 w-fit">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition ${
                    activeTab === t.key
                      ? 'bg-white text-violet-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Chart card */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-1 flex items-center gap-2">
                <h2 className="font-semibold text-slate-800">
                  {TABS.find((t) => t.key === activeTab)?.label} Overview
                </h2>
                <span className="text-xs text-slate-400">({activeRows.length} entries)</span>
              </div>
              <ChartPanel rows={activeRows} groupKey={activeTab} />
            </div>

            {/* Rank table */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-3.5">
                <h2 className="font-semibold text-slate-800">
                  {TABS.find((t) => t.key === activeTab)?.label} Ranking
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400">#</th>
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400">
                        {activeTab === 'lecturer' ? 'Lecturer' : activeTab === 'subject' ? 'Subject' : 'Batch'}
                      </th>
                      {activeTab === 'subject' && (
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400">Lecturer</th>
                      )}
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">Repeat</th>
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">Not Submitted</th>
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">Total</th>
                      <th className="px-4 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.map((row, i) => (
                      <tr
                        key={i}
                        onClick={() => setModalEntry(row)}
                        className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-violet-50"
                      >
                        <td className="px-4 py-2.5 text-xs text-slate-400">{i + 1}</td>
                        <td className="px-4 py-2.5 text-xs font-medium text-slate-800">{row.label}</td>
                        {activeTab === 'subject' && (
                          <td className="px-4 py-2.5 text-xs text-slate-500">{row.lecturerName || '—'}</td>
                        )}
                        <td className="px-4 py-2.5 text-right">
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">{row.repeatCount}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{row.notSubmittedCount}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{row.total}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="text-[11px] text-violet-500">View →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && !error && data?.courses?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <p className="text-sm">No data found for the selected filters.</p>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {modalEntry && (
        <DetailModal
          entry={modalEntry}
          groupKey={activeTab}
          onClose={() => setModalEntry(null)}
        />
      )}
    </div>
  );
}
